import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { readPhidFromSearch } from "@veritrellis/analytics";
import { analytics } from "../lib/analytics.js";
import { apiFetch } from "../lib/api.js";
import type { AdminMeData } from "../hooks/useAdminMe.js";
import { readWorkspaceIdFromSession } from "../lib/workspaceStorage.js";

const fetchAdminMeForAnalytics = async (): Promise<AdminMeData | null> => {
  const res = await apiFetch("/admin/me");
  if (res.status === 401) {
    return null;
  }
  if (!res.ok) {
    return null;
  }
  return (await res.json()) as AdminMeData;
};

export const AnalyticsProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const meQ = useQuery({
    queryKey: ["admin", "me", "analytics"],
    queryFn: fetchAdminMeForAnalytics,
    retry: false
  });

  useEffect(() => {
    if (location.pathname.startsWith("/auth/logout")) {
      analytics.resetUser();
      return;
    }
    analytics.trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const me = meQ.data;
    if (me === undefined || me === null) {
      return;
    }
    const phid = readPhidFromSearch(location.search);
    if (phid !== null) {
      analytics.aliasDistinctId(me.user.id);
    }
    const storedWorkspaceId = readWorkspaceIdFromSession();
    const workspaceIds = new Set(me.workspaces.map((w) => w.id));
    const activeWorkspaceId =
      storedWorkspaceId !== null && workspaceIds.has(storedWorkspaceId)
        ? storedWorkspaceId
        : (me.workspaces[0]?.id ?? null);
    const activeRole = me.workspaces.find((w) => w.id === activeWorkspaceId)?.role ?? null;
    analytics.identifyUser({
      userId: me.user.id,
      workspaceId: activeWorkspaceId,
      role: activeRole
    });
  }, [location.search, meQ.data]);

  return children;
};
